import { useState, useEffect } from 'react';
import { supabase } from '../../../api/supabase';
import Papa from 'papaparse';
import theme from '../../../theme/globalTheme';

const ImprovedCharterClientSystem = () => {
  // State management
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('Données Combinées 2023-2025');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [calculations, setCalculations] = useState({});
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedCharterType, setSelectedCharterType] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [paginatedData, setPaginatedData] = useState([]);
  
  // Client management state
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientSidebar, setShowClientSidebar] = useState(false);
  const [clientForm, setClientForm] = useState({
    client_name: '',
    email: '',
    phone: '',
    contact_info: '',
    notes: '',
    other_info: ''
  });
  const [clientImages, setClientImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Client pagination state
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [currentClientPage, setCurrentClientPage] = useState(1);
  const [clientsPerPage, setClientsPerPage] = useState(12);
  const [paginatedClients, setPaginatedClients] = useState([]);
  
  // Enhanced charter assignment state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [charterToLink, setCharterToLink] = useState(null);
  const [selectedTargetClient, setSelectedTargetClient] = useState('');
  const [linkSearchTerm, setLinkSearchTerm] = useState('');
  const [filteredLinkClients, setFilteredLinkClients] = useState([]);
  const [showCreateFromCharter, setShowCreateFromCharter] = useState(false);
  
  // Enhanced client search and matching
  const [clientNameAnalysis, setClientNameAnalysis] = useState({});
  const [charterLinks, setCharterLinks] = useState({}); // Maps charter data to client_id
  
  // Mode toggle
  const [currentMode, setCurrentMode] = useState('analysis');

  // Effects
  useEffect(() => {
    loadCombinedData();
    loadClients();
    loadCharterLinks();
  }, []);

  useEffect(() => {
    loadFiles();
  }, []);

  useEffect(() => {
    filterData();
  }, [searchTerm, data, dateRange, selectedCharterType, selectedPaymentMethod]);

  useEffect(() => {
    calculateStats();
  }, [filteredData]);

  useEffect(() => {
    updatePagination();
  }, [filteredData, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateRange, selectedCharterType, selectedPaymentMethod]);

  useEffect(() => {
    filterClients();
  }, [clients, clientSearchTerm]);

  useEffect(() => {
    updateClientPagination();
  }, [filteredClients, currentClientPage, clientsPerPage]);

  useEffect(() => {
    setCurrentClientPage(1);
  }, [clientSearchTerm]);

  useEffect(() => {
    if (data.length > 0 && clients.length > 0) {
      analyzeClientNames();
    }
  }, [data, clients, charterLinks]);

  useEffect(() => {
    if (showLinkModal) {
      filterLinkClients();
    }
  }, [linkSearchTerm, clients, charterToLink]);

  // Core functions
  const analyzeClientNames = () => {
    const analysis = {};
    
    data.forEach((charter, index) => {
      const charterKey = `${charter.client_name}_${charter.charter_date}_${charter.total_amount}`;
      
      const isLinked = charterLinks[charterKey];
      
      const exactMatch = clients.find(client => 
        client.client_name.toLowerCase() === charter.client_name.toLowerCase()
      );
      
      const similarMatches = clients.filter(client => {
        const clientName = client.client_name.toLowerCase();
        const charterName = charter.client_name.toLowerCase();
        
        return (
          clientName.includes(charterName) || 
          charterName.includes(clientName) ||
          levenshteinDistance(clientName, charterName) <= 2
        );
      });
      
      const occurrences = data.filter(d => 
        d.client_name.toLowerCase() === charter.client_name.toLowerCase()
      ).length;
      
      analysis[charterKey] = {
        charter,
        index,
        exactMatch,
        similarMatches: similarMatches.filter(m => m.client_id !== exactMatch?.client_id),
        occurrences,
        isLinked,
        linkedClient: isLinked ? clients.find(c => c.client_id === isLinked) : null,
        status: isLinked ? 'linked' : exactMatch ? 'exact_match' : similarMatches.length > 0 ? 'similar_match' : 'no_match'
      };
    });
    
    setClientNameAnalysis(analysis);
  };

  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  const loadCharterLinks = async () => {
    try {
      const { data: linksData, error } = await supabase
        .from('charter_links')
        .select('*');

      if (!error && linksData) {
        const links = {};
        linksData.forEach(link => {
          links[link.charter_key] = link.client_id;
        });
        setCharterLinks(links);
      } else {
        const savedLinks = localStorage.getItem('charter_client_links');
        if (savedLinks) {
          setCharterLinks(JSON.parse(savedLinks));
        }
      }
    } catch (error) {
      console.error('Error loading charter links:', error);
      const savedLinks = localStorage.getItem('charter_client_links');
      if (savedLinks) {
        setCharterLinks(JSON.parse(savedLinks));
      }
    }
  };

  const saveCharterLink = async (charterKey, clientId) => {
    try {
      const newLinks = { ...charterLinks, [charterKey]: clientId };
      setCharterLinks(newLinks);
      
      const { error } = await supabase
        .from('charter_links')
        .upsert([{
          charter_key: charterKey,
          client_id: clientId,
          created_at: new Date().toISOString()
        }]);

      if (error) {
        localStorage.setItem('charter_client_links', JSON.stringify(newLinks));
      }
    } catch (error) {
      console.error('Error saving charter link:', error);
      const newLinks = { ...charterLinks, [charterKey]: clientId };
      localStorage.setItem('charter_client_links', JSON.stringify(newLinks));
    }
  };

  const removeCharterLink = async (charterKey) => {
    try {
      const newLinks = { ...charterLinks };
      delete newLinks[charterKey];
      setCharterLinks(newLinks);
      
      const { error } = await supabase
        .from('charter_links')
        .delete()
        .eq('charter_key', charterKey);

      if (error) {
        localStorage.setItem('charter_client_links', JSON.stringify(newLinks));
      }
    } catch (error) {
      console.error('Error removing charter link:', error);
      const newLinks = { ...charterLinks };
      delete newLinks[charterKey];
      localStorage.setItem('charter_client_links', JSON.stringify(newLinks));
    }
  };

  const getCharterKey = (charter) => {
    return `${charter.client_name}_${charter.charter_date}_${charter.total_amount}`;
  };

  const filterClients = () => {
    let filtered = [...clients];
    
    if (clientSearchTerm) {
      const term = clientSearchTerm.toLowerCase();
      filtered = filtered.filter(client =>
        client.client_name?.toLowerCase().includes(term) ||
        client.email?.toLowerCase().includes(term) ||
        client.phone?.includes(term) ||
        client.notes?.toLowerCase().includes(term)
      );
    }
    
    setFilteredClients(filtered);
  };

  const updateClientPagination = () => {
    const startIndex = (currentClientPage - 1) * clientsPerPage;
    const endIndex = startIndex + clientsPerPage;
    setPaginatedClients(filteredClients.slice(startIndex, endIndex));
  };

  const getClientTotalPages = () => {
    return Math.ceil(filteredClients.length / clientsPerPage);
  };

  const filterLinkClients = () => {
    let filtered = [...clients];
    
    if (linkSearchTerm) {
      const term = linkSearchTerm.toLowerCase();
      filtered = filtered.filter(client =>
        client.client_name?.toLowerCase().includes(term) ||
        client.email?.toLowerCase().includes(term)
      );
    }
    
    if (charterToLink) {
      const charterName = charterToLink.client_name.toLowerCase();
      filtered.sort((a, b) => {
        const aName = a.client_name.toLowerCase();
        const bName = b.client_name.toLowerCase();
        
        if (aName === charterName && bName !== charterName) return -1;
        if (bName === charterName && aName !== charterName) return 1;
        
        const aDistance = levenshteinDistance(aName, charterName);
        const bDistance = levenshteinDistance(bName, charterName);
        
        return aDistance - bDistance;
      });
    }
    
    setFilteredLinkClients(filtered.slice(0, 20));
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatIntegerNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const loadClients = async () => {
    try {
      const { data: clientsData, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClients(clientsData || []);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    }
  };

  const loadClientImages = async (clientId) => {
    try {
      const { data: imagesData, error } = await supabase
        .from('client_images')
        .select('*')
        .eq('client_id', clientId)
        .order('upload_date', { ascending: false });

      if (error) throw error;
      setClientImages(imagesData || []);
    } catch (error) {
      console.error('Erreur lors du chargement des images:', error);
    }
  };

  const saveClient = async () => {
    try {
      let savedClient;
      
      if (selectedClient) {
        const { data, error } = await supabase
          .from('clients')
          .update(clientForm)
          .eq('client_id', selectedClient.client_id)
          .select()
          .single();

        if (error) throw error;
        savedClient = data;
      } else {
        const { data, error } = await supabase
          .from('clients')
          .insert([clientForm])
          .select()
          .single();

        if (error) throw error;
        savedClient = data;
        
        if (showCreateFromCharter && charterToLink) {
          const charterKey = getCharterKey(charterToLink);
          await saveCharterLink(charterKey, savedClient.client_id);
        }
      }

      await loadClients();
      closeSidebar();
      
      if (showLinkModal && charterToLink) {
        setSelectedTargetClient(savedClient.client_id);
      }
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du client:', error);
      alert('Erreur lors de la sauvegarde du client');
    }
  };

  const uploadClientImage = async (file, clientId) => {
    try {
      setUploadingImage(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${clientId}_${Date.now()}.${fileExt}`;
      const filePath = `client_images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase
        .from('client_images')
        .insert([{
          client_id: clientId,
          image_path: filePath,
          is_primary: clientImages.length === 0
        }]);

      if (dbError) throw dbError;

      await loadClientImages(clientId);
    } catch (error) {
      console.error('Erreur lors du téléchargement de l\'image:', error);
      alert('Erreur lors du téléchargement de l\'image');
    } finally {
      setUploadingImage(false);
    }
  };

  const openClientSidebar = (client = null, fromCharterData = null) => {
    if (client) {
      setSelectedClient(client);
      setClientForm({
        client_name: client.client_name,
        email: client.email || '',
        phone: client.phone || '',
        contact_info: client.contact_info || '',
        notes: client.notes || '',
        other_info: client.other_info || ''
      });
      loadClientImages(client.client_id);
    } else if (fromCharterData) {
      setSelectedClient(null);
      setClientForm({
        client_name: fromCharterData,
        email: '',
        phone: '',
        contact_info: '',
        notes: '',
        other_info: ''
      });
      setClientImages([]);
    } else {
      setSelectedClient(null);
      setClientForm({
        client_name: '',
        email: '',
        phone: '',
        contact_info: '',
        notes: '',
        other_info: ''
      });
      setClientImages([]);
    }
    setShowClientSidebar(true);
  };

  const closeSidebar = () => {
    setShowClientSidebar(false);
    setSelectedClient(null);
    setShowCreateFromCharter(false);
    setClientForm({
      client_name: '',
      email: '',
      phone: '',
      contact_info: '',
      notes: '',
      other_info: ''
    });
    setClientImages([]);
  };

  const openLinkModal = (charter) => {
    setCharterToLink(charter);
    setSelectedTargetClient('');
    setLinkSearchTerm('');
    setShowLinkModal(true);
    setFilteredLinkClients([]);
  };

  const linkCharter = async () => {
    if (!selectedTargetClient || !charterToLink) return;

    try {
      const charterKey = getCharterKey(charterToLink);
      await saveCharterLink(charterKey, selectedTargetClient);

      setShowLinkModal(false);
      setCharterToLink(null);
      setSelectedTargetClient('');
      setLinkSearchTerm('');
    } catch (error) {
      console.error('Erreur lors du lien:', error);
      alert('Erreur lors du lien du charter');
    }
  };

  const unlinkCharter = async (charter) => {
    try {
      const charterKey = getCharterKey(charter);
      await removeCharterLink(charterKey);
    } catch (error) {
      console.error('Erreur lors du délien:', error);
      alert('Erreur lors du délien du charter');
    }
  };

  const getClientCharters = (clientId) => {
    return data.filter(charter => {
      const charterKey = getCharterKey(charter);
      return charterLinks[charterKey] === clientId;
    });
  };

  const getClientStats = (clientId) => {
    const charters = getClientCharters(clientId);
    return {
      count: charters.length,
      total: charters.reduce((sum, charter) => sum + charter.total_amount, 0)
    };
  };

  const loadFiles = async () => {
    try {
      const { data: fileList, error } = await supabase.storage
        .from('media')
        .list('', {
          limit: 100,
          offset: 0
        });

      if (error) throw error;
      
      const csvFiles = fileList.filter(file => 
        file.name.toLowerCase().endsWith('.csv')
      );
      
      setFiles(csvFiles);
    } catch (error) {
      console.error('Erreur lors du chargement des fichiers:', error);
    }
  };

  const loadCombinedData = async () => {
    setLoading(true);
    
    try {
      const filesToLoad = ['2023.csv', '2024.csv', '2025.csv'];
      const allData = [];
      
      for (const fileName of filesToLoad) {
        console.log(`Chargement de ${fileName}...`);
        
        const response = await fetch(`https://vlakljvouimjemxbcnmo.supabase.co/storage/v1/object/public/media/${fileName}`);
        
        if (!response.ok) {
          console.warn(`Échec du chargement de ${fileName}, passage au suivant...`);
          continue;
        }
        
        const text = await response.text();
        
        await new Promise((resolve, reject) => {
          Papa.parse(text, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => {
              const headerMap = {
                'DATE': 'purchase_date',
                'MODE DE PAIEMENT': 'payment_method',
                'TYPE DE CHARTER': 'charter_type',
                'DATE CHARTER': 'charter_date',
                'MONTANT TOTAL (CB/ONLINE/FACTURE)': 'total_amount',
                'MONTANT PAYE CASH': 'cash_amount',
                'PRIX DU BATEAU': 'boat_price',
                'TAXES': 'taxes',
                'VIP SERVICES': 'vip_services',
                'NOM CLIENT': 'client_name',
                'NOM PAYEUR': 'payer_name',
                'CHARTERER': 'charterer'
              };
              return headerMap[header] || header.toLowerCase().replace(/\s+/g, '_');
            },
            complete: (results) => {
              const processedData = results.data.map(row => ({
                ...row,
                total_amount: parseEuropeanNumber(row.total_amount) || 0,
                cash_amount: parseEuropeanNumber(row.cash_amount) || 0,
                boat_price: parseEuropeanNumber(row.boat_price) || 0,
                taxes: parseEuropeanNumber(row.taxes) || 0,
                vip_services: parseEuropeanNumber(row.vip_services) || 0,
                purchase_date: row.purchase_date?.trim(),
                charter_date: row.charter_date?.trim(),
                payment_method: row.payment_method?.trim(),
                charter_type: row.charter_type?.trim(),
                client_name: row.client_name?.trim(),
                payer_name: row.payer_name?.trim(),
                charterer: row.charterer?.trim(),
                source_file: fileName,
                year: fileName.includes('2023') ? '2023' : fileName.includes('2024') ? '2024' : fileName.includes('2025') ? '2025' : 'Inconnu'
              }));
              
              allData.push(...processedData);
              console.log(`Chargé ${processedData.length} enregistrements de ${fileName}`);
              resolve();
            },
            error: (error) => {
              console.error(`Erreur de parsing CSV pour ${fileName}:`, error);
              resolve();
            }
          });
        });
      }
      
      allData.sort((a, b) => {
        const dateA = new Date(convertDateFormat(a.charter_date || a.purchase_date));
        const dateB = new Date(convertDateFormat(b.charter_date || b.purchase_date));
        return dateB - dateA;
      });
      
      console.log(`Total des enregistrements combinés: ${allData.length}`);
      setData(allData);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données combinées:', error);
      setLoading(false);
    }
  };

  const filterData = () => {
    let filtered = [...data];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(row =>
        row.client_name?.toLowerCase().includes(term) ||
        row.payer_name?.toLowerCase().includes(term) ||
        row.charterer?.toLowerCase().includes(term) ||
        row.charter_type?.toLowerCase().includes(term) ||
        row.payment_method?.toLowerCase().includes(term)
      );
    }

    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(row => {
        const rowDate = new Date(convertDateFormat(row.charter_date || row.purchase_date));
        const startDate = dateRange.start ? new Date(dateRange.start) : null;
        const endDate = dateRange.end ? new Date(dateRange.end) : null;
        
        if (startDate && rowDate < startDate) return false;
        if (endDate && rowDate > endDate) return false;
        return true;
      });
    }

    if (selectedCharterType) {
      filtered = filtered.filter(row => 
        row.charter_type?.toLowerCase().includes(selectedCharterType.toLowerCase())
      );
    }

    if (selectedPaymentMethod) {
      filtered = filtered.filter(row => 
        row.payment_method?.toLowerCase().includes(selectedPaymentMethod.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  const updatePagination = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedData(filteredData.slice(startIndex, endIndex));
  };

  const getTotalPages = () => {
    return Math.ceil(filteredData.length / itemsPerPage);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const convertDateFormat = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  const calculateStats = () => {
    if (filteredData.length === 0) {
      setCalculations({});
      return;
    }

    const stats = {
      totalRecords: filteredData.length,
      totalAmount: filteredData.reduce((sum, row) => sum + row.total_amount, 0),
      totalCash: filteredData.reduce((sum, row) => sum + row.cash_amount, 0),
      totalVipServices: filteredData.reduce((sum, row) => sum + row.vip_services, 0),
      averageAmount: filteredData.reduce((sum, row) => sum + row.total_amount, 0) / filteredData.length,
      
      paymentMethods: filteredData.reduce((acc, row) => {
        const method = row.payment_method || 'Inconnu';
        if (!acc[method]) {
          acc[method] = { count: 0, total: 0 };
        }
        acc[method].count++;
        acc[method].total += row.total_amount;
        return acc;
      }, {}),
      
      charterTypes: filteredData.reduce((acc, row) => {
        const type = row.charter_type || 'Inconnu';
        if (!acc[type]) {
          acc[type] = { count: 0, total: 0 };
        }
        acc[type].count++;
        acc[type].total += row.total_amount;
        return acc;
      }, {}),
      
      yearBreakdown: filteredData.reduce((acc, row) => {
        const year = row.year || 'Inconnu';
        if (!acc[year]) {
          acc[year] = { count: 0, total: 0 };
        }
        acc[year].count++;
        acc[year].total += row.total_amount;
        return acc;
      }, {})
    };

    setCalculations(stats);
  };

  const getUniqueValues = (field) => {
    return [...new Set(data.map(row => row[field]).filter(Boolean))];
  };

  const parseEuropeanNumber = (value) => {
    if (!value || typeof value === 'number') return value;
    
    let cleanValue = value.toString().trim();
    cleanValue = cleanValue.replace(/[€$£¥₹]/g, '');
    cleanValue = cleanValue.trim();
    
    if (cleanValue.includes(',')) {
      cleanValue = cleanValue.replace(/[\s\.]/g, '');
      cleanValue = cleanValue.replace(',', '.');
    } else {
      const parts = cleanValue.split('.');
      if (parts.length > 2 || (parts.length === 2 && parts[1].length > 2)) {
        cleanValue = cleanValue.replace(/\./g, '');
      }
    }
    
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Pagination Components
  const PaginationControls = () => {
    const totalPages = getTotalPages();
    const maxVisiblePages = 3;
    
    if (totalPages <= 1) return null;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '10px', 
        padding: '20px',
        backgroundColor: theme.colors.glassMedium,
        borderRadius: '4px',
        marginTop: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label>Éléments par page:</label>
          <select 
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <button 
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            style={{ 
              padding: '8px 12px', 
              backgroundColor: currentPage === 1 ? '#e9ecef' : '#007bff',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: 'none', 
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Premier
          </button>
          
          <button 
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={{ 
              padding: '8px 12px', 
              backgroundColor: currentPage === 1 ? '#e9ecef' : '#007bff',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: 'none', 
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Précédent
          </button>
          
          {pageNumbers.map(page => (
            <button 
              key={page}
              onClick={() => goToPage(page)}
              style={{ 
                padding: '8px 12px', 
                backgroundColor: page === currentPage ? '#28a745' : '#007bff',
                color: theme.colors.bgDark,
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: page === currentPage ? 'bold' : 'normal'
              }}
            >
              {page}
            </button>
          ))}
          
          <button 
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{ 
              padding: '8px 12px', 
              backgroundColor: currentPage === totalPages ? '#e9ecef' : '#007bff',
              color: currentPage === totalPages ? '#6c757d' : 'white',
              border: 'none', 
              borderRadius: '4px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Suivant
          </button>
          
          <button 
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            style={{ 
              padding: '8px 12px', 
              backgroundColor: currentPage === totalPages ? '#e9ecef' : '#007bff',
              color: currentPage === totalPages ? '#6c757d' : 'white',
              border: 'none', 
              borderRadius: '4px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Dernier
          </button>
        </div>
        
        <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
          Affichage de {((currentPage - 1) * itemsPerPage) + 1} à {Math.min(currentPage * itemsPerPage, filteredData.length)} sur {formatIntegerNumber(filteredData.length)} enregistrements
        </div>
      </div>
    );
  };

  const ClientPaginationControls = () => {
    const totalPages = getClientTotalPages();
    const maxVisiblePages = 5;
    
    if (totalPages <= 1) return null;

    let startPage = Math.max(1, currentClientPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '10px', 
        padding: '20px',
        backgroundColor: theme.colors.glassMedium,
        borderRadius: '8px',
        marginTop: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontSize: '14px', color: theme.colors.textPrimary }}>Clients par page:</label>
          <select 
            value={clientsPerPage}
            onChange={(e) => {
              setClientsPerPage(Number(e.target.value));
              setCurrentClientPage(1);
            }}
            style={{ 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: '1px solid #ddd',
              backgroundColor: 'white',
              fontSize: '14px'
            }}
          >
            <option value={6}>6</option>
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={48}>48</option>
          </select>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button 
            onClick={() => setCurrentClientPage(1)}
            disabled={currentClientPage === 1}
            style={{ 
              padding: '8px 12px', 
              backgroundColor: currentClientPage === 1 ? '#e9ecef' : '#007bff',
              color: currentClientPage === 1 ? '#6c757d' : 'white',
              border: 'none', 
              borderRadius: '6px',
              cursor: currentClientPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            ⏮️
          </button>
          
          <button 
            onClick={() => setCurrentClientPage(currentClientPage - 1)}
            disabled={currentClientPage === 1}
            style={{ 
              padding: '8px 12px', 
              backgroundColor: currentClientPage === 1 ? '#e9ecef' : '#007bff',
              color: currentClientPage === 1 ? '#6c757d' : 'white',
              border: 'none', 
              borderRadius: '6px',
              cursor: currentClientPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            ⏪
          </button>
          
          {pageNumbers.map(page => (
            <button 
              key={page}
              onClick={() => setCurrentClientPage(page)}
              style={{ 
                padding: '8px 12px', 
                backgroundColor: page === currentClientPage ? '#28a745' : '#007bff',
                color: theme.colors.bgDark,
                border: 'none', 
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: page === currentClientPage ? 'bold' : 'normal',
                fontSize: '14px',
                minWidth: '40px'
              }}
            >
              {page}
            </button>
          ))}
          
          <button 
            onClick={() => setCurrentClientPage(currentClientPage + 1)}
            disabled={currentClientPage === totalPages}
            style={{ 
              padding: '8px 12px', 
              backgroundColor: currentClientPage === totalPages ? '#e9ecef' : '#007bff',
              color: currentClientPage === totalPages ? '#6c757d' : 'white',
              border: 'none', 
              borderRadius: '6px',
              cursor: currentClientPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            ⏩
          </button>
          
          <button 
            onClick={() => setCurrentClientPage(totalPages)}
            disabled={currentClientPage === totalPages}
            style={{ 
              padding: '8px 12px', 
              backgroundColor: currentClientPage === totalPages ? '#e9ecef' : '#007bff',
              color: currentClientPage === totalPages ? '#6c757d' : 'white',
              border: 'none', 
              borderRadius: '6px',
              cursor: currentClientPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            ⏭️
          </button>
        </div>
        
        <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
          Page {currentClientPage} sur {totalPages} • {formatIntegerNumber(filteredClients.length)} client{filteredClients.length > 1 ? 's' : ''}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgLight, padding: '20px' }}>
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', position: 'relative' }}>
      {/* Header with Mode Toggle */}
      <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: theme.colors.glassMedium, borderRadius: theme.borderRadius.card, border: `1px solid ${theme.colors.glassBorder}` }}>
        <h1 style={{ margin: '0 0 20px 0', color: theme.colors.textPrimary, fontSize: '28px', fontWeight: '600' }}>
          🚤 Système Hybride Charter & Clients Amélioré
        </h1>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setCurrentMode('analysis')}
            style={{
              padding: '12px 24px',
              backgroundColor: currentMode === 'analysis' ? '#1890ff' : '#6c757d',
              color: theme.colors.bgDark,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              boxShadow: currentMode === 'analysis' ? '0 2px 8px rgba(24, 144, 255, 0.3)' : 'none'
            }}
          >
            📊 Analyse des Charters
          </button>
          
          <button
            onClick={() => setCurrentMode('clients')}
            style={{
              padding: '12px 24px',
              backgroundColor: currentMode === 'clients' ? '#1890ff' : '#6c757d',
              color: theme.colors.bgDark,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              boxShadow: currentMode === 'clients' ? '0 2px 8px rgba(24, 144, 255, 0.3)' : 'none'
            }}
          >
            👥 Gestion des Clients
          </button>
        </div>
        
        {/* Link Statistics */}
        {Object.keys(charterLinks).length > 0 && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: theme.colors.glassMedium,
            borderRadius: theme.borderRadius.sm,
            border: `1px solid ${theme.colors.glassBorderHover}`
          }}>
            <span style={{ fontSize: '14px', color: theme.colors.textPrimary, fontWeight: '500' }}>
              🔗 {Object.keys(charterLinks).length} charters liés à des clients
            </span>
          </div>
        )}
      </div>

      {/* Charter Analysis Mode */}
      {currentMode === 'analysis' && (
        <>
          {/* Quick Actions */}
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: theme.colors.glassMedium, borderRadius: theme.borderRadius.sm, border: `1px solid ${theme.colors.glassBorder}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: '0 0 5px 0', color: theme.colors.textPrimary, fontSize: '16px' }}>Actions Rapides</h3>
                <p style={{ margin: 0, fontSize: '14px', color: theme.colors.textSecondary }}>
                  Chargez vos données depuis les fichiers CSV
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  onClick={loadCombinedData}
                  disabled={loading}
                  style={{ 
                    padding: '10px 20px', 
                    backgroundColor: loading ? '#95a5a6' : '#52c41a', 
                    color: theme.colors.bgDark, 
                    border: 'none', 
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  🔄 Recharger CSV
                </button>
                <button 
                  onClick={loadClients}
                  disabled={loading}
                  style={{ 
                    padding: '10px 20px', 
                    backgroundColor: loading ? '#95a5a6' : '#1890ff', 
                    color: theme.colors.bgDark, 
                    border: 'none', 
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  🗄️ Recharger Clients
                </button>
              </div>
            </div>
          </div>

          {loading && (
            <div style={{ padding: '40px', textAlign: 'center', backgroundColor: theme.colors.glassMedium, borderRadius: theme.borderRadius.sm, marginBottom: '20px' }}>
              <div style={{ fontSize: '18px', marginBottom: '10px', color: theme.colors.textPrimary }}>⏳ Chargement des données...</div>
              <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>Veuillez patienter pendant le traitement des données</div>
            </div>
          )}

          {data.length > 0 && (
            <>
              {/* Search and Filters */}
              <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: theme.colors.glassMedium, borderRadius: '8px', border: `1px solid ${theme.colors.glassBorder}` }}>
                <h3 style={{ marginTop: 0, color: theme.colors.textPrimary, fontSize: '18px', marginBottom: '20px' }}>🔍 Recherche et Filtres</h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.colors.textPrimary }}>Recherche:</label>
                    <input 
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Rechercher par client, payeur, type..."
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        borderRadius: '6px', 
                        border: `1px solid ${theme.colors.glassBorder}`,
                        fontSize: '14px',
                        transition: 'border-color 0.2s ease'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.colors.textPrimary }}>Date de Début:</label>
                    <input 
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        borderRadius: '6px', 
                        border: `1px solid ${theme.colors.glassBorder}`,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.colors.textPrimary }}>Date de Fin:</label>
                    <input 
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        borderRadius: '6px', 
                        border: `1px solid ${theme.colors.glassBorder}`,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.colors.textPrimary }}>Type de Charter:</label>
                    <select 
                      value={selectedCharterType}
                      onChange={(e) => setSelectedCharterType(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        borderRadius: '6px', 
                        border: `1px solid ${theme.colors.glassBorder}`,
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Tous les Types</option>
                      {getUniqueValues('charter_type').map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.colors.textPrimary }}>Mode de Paiement:</label>
                    <select 
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        borderRadius: '6px', 
                        border: `1px solid ${theme.colors.glassBorder}`,
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Toutes les Méthodes</option>
                      {getUniqueValues('payment_method').map(method => (
                        <option key={method} value={method}>{method}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setDateRange({ start: '', end: '' });
                    setSelectedCharterType('');
                    setSelectedPaymentMethod('');
                  }}
                  style={{ 
                    marginTop: '16px', 
                    padding: '10px 20px', 
                    backgroundColor: theme.colors.darkGrey, 
                    color: theme.colors.bgDark, 
                    border: 'none', 
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Effacer les Filtres
                </button>
              </div>

              {/* Statistics */}
              {calculations.totalRecords && (
                <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: theme.colors.glassMedium, borderRadius: '8px', border: `1px solid ${theme.colors.glassBorder}` }}>
                  <h3 style={{ marginTop: 0, color: theme.colors.textPrimary, fontSize: '18px', marginBottom: '20px' }}>
                    📈 Statistiques ({formatIntegerNumber(filteredData.length)} enregistrements)
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: theme.colors.glassMedium, 
                      borderRadius: '8px',
                      border: `1px solid ${theme.colors.glassBorder}`
                    }}>
                      <div style={{ fontSize: '14px', color: theme.colors.white, fontWeight: '500', marginBottom: '4px' }}>💰 Montant Total</div>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: theme.colors.textPrimary }}>
                        €{formatNumber(calculations.totalAmount)}
                      </div>
                    </div>
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: theme.colors.glassMedium, 
                      borderRadius: '8px',
                      border: `1px solid ${theme.colors.glassBorder}`
                    }}>
                      <div style={{ fontSize: '14px', color: theme.colors.textPrimary, fontWeight: '500', marginBottom: '4px' }}>💵 Total Cash</div>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: theme.colors.textPrimary }}>
                        €{formatNumber(calculations.totalCash)}
                      </div>
                    </div>
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: theme.colors.glassMedium, 
                      borderRadius: '8px',
                      border: `1px solid ${theme.colors.glassBorder}`
                    }}>
                      <div style={{ fontSize: '14px', color: theme.colors.textPrimary, fontWeight: '500', marginBottom: '4px' }}>📊 Montant Moyen</div>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: theme.colors.textPrimary }}>
                        €{formatNumber(calculations.averageAmount)}
                      </div>
                    </div>
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: theme.colors.glassMedium, 
                      borderRadius: '8px',
                      border: '1px solid #d3adf7'
                    }}>
                      <div style={{ fontSize: '14px', color: theme.colors.textPrimary, fontWeight: '500', marginBottom: '4px' }}>✨ Services VIP</div>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: theme.colors.textPrimary }}>
                        €{formatNumber(calculations.totalVipServices)}
                      </div>
                    </div>
                  </div>

                  {/* Year Breakdown */}
                  {calculations.yearBreakdown && Object.keys(calculations.yearBreakdown).length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                      <h4 style={{ margin: '0 0 16px 0', color: theme.colors.textPrimary, fontSize: '16px' }}>📅 Répartition par Année</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                        {Object.entries(calculations.yearBreakdown)
                          .sort(([a], [b]) => b.localeCompare(a))
                          .map(([year, stats]) => (
                          <div key={year} style={{ 
                            padding: '16px', 
                            backgroundColor: year === '2025' ? '#e6f7ff' : year === '2024' ? '#f6ffed' : '#fff7e6', 
                            borderRadius: '8px', 
                            border: year === '2025' ? '2px solid #1890ff' : '1px solid #d9d9d9'
                          }}>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: theme.colors.textPrimary, marginBottom: '8px' }}>
                              {year} {year === '2025' ? '🆕' : ''}
                            </div>
                            <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '4px' }}>
                              {formatIntegerNumber(stats.count)} enregistrements
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: '600', color: theme.colors.white }}>
                              €{formatNumber(stats.total)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Pagination Controls */}
              <PaginationControls />

              {/* Enhanced Data Table */}
              <div style={{ overflowX: 'auto', marginTop: '20px', backgroundColor: theme.colors.glassMedium, borderRadius: '8px', border: `1px solid ${theme.colors.glassBorder}` }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: theme.colors.white, color: theme.colors.bgDark }}>
                      <th style={{ padding: '12px', border: 'none', fontSize: '14px', fontWeight: '600' }}>Année</th>
                      <th style={{ padding: '12px', border: 'none', fontSize: '14px', fontWeight: '600' }}>Date</th>
                      <th style={{ padding: '12px', border: 'none', fontSize: '14px', fontWeight: '600' }}>Paiement</th>
                      <th style={{ padding: '12px', border: 'none', fontSize: '14px', fontWeight: '600' }}>Type Charter</th>
                      <th style={{ padding: '12px', border: 'none', fontSize: '14px', fontWeight: '600' }}>Date Charter</th>
                      <th style={{ padding: '12px', border: 'none', fontSize: '14px', fontWeight: '600' }}>Montant Total</th>
                      <th style={{ padding: '12px', border: 'none', fontSize: '14px', fontWeight: '600' }}>Client</th>
                      <th style={{ padding: '12px', border: 'none', fontSize: '14px', fontWeight: '600' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((row, index) => {
                      const charterKey = getCharterKey(row);
                      const analysis = clientNameAnalysis[charterKey];
                      
                      return (
                        <tr key={index} style={{ 
                          backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                          borderBottom: '1px solid #f0f0f0'
                        }}>
                          <td style={{ 
                            padding: '12px', 
                            border: 'none',
                            backgroundColor: row.year === '2025' ? '#e6f7ff' : row.year === '2024' ? '#f6ffed' : '#fff7e6',
                            fontWeight: '600',
                            fontSize: '14px'
                          }}>
                            {row.year} {row.year === '2025' ? '🆕' : ''}
                          </td>
                          <td style={{ padding: '12px', border: 'none', fontSize: '14px' }}>{row.purchase_date}</td>
                          <td style={{ padding: '12px', border: 'none', fontSize: '14px' }}>{row.payment_method}</td>
                          <td style={{ padding: '12px', border: 'none', fontSize: '14px' }}>{row.charter_type}</td>
                          <td style={{ padding: '12px', border: 'none', fontSize: '14px' }}>{row.charter_date}</td>
                          <td style={{ padding: '12px', border: 'none', fontSize: '14px', fontWeight: '600', color: theme.colors.white }}>
                            €{formatNumber(row.total_amount)}
                          </td>
                          <td style={{ 
                            padding: '12px', 
                            border: 'none',
                            backgroundColor: analysis?.status === 'linked' ? '#f6ffed' : 
                                          analysis?.status === 'exact_match' ? '#fff7e6' : 
                                          analysis?.status === 'similar_match' ? '#fff1f0' : 'transparent'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              {analysis?.status === 'linked' && <span style={{ color: theme.colors.white, fontSize: '16px' }}>✅</span>}
                              {analysis?.status === 'exact_match' && <span style={{ color: theme.colors.textPrimary, fontSize: '16px' }}>👤</span>}
                              {analysis?.status === 'similar_match' && <span style={{ color: '#ff4d4f', fontSize: '16px' }}>⚠️</span>}
                              {analysis?.status === 'no_match' && <span style={{ color: theme.colors.textTertiary, fontSize: '16px' }}>❓</span>}
                              
                              <span style={{ fontSize: '14px' }}>
                                {analysis?.isLinked && analysis?.linkedClient ? 
                                  analysis.linkedClient.client_name : 
                                  row.client_name}
                              </span>
                              
                              {analysis?.status === 'linked' && (
                                <span style={{ 
                                  fontSize: '10px', 
                                  backgroundColor: theme.colors.lightGrey, 
                                  color: theme.colors.bgDark, 
                                  padding: '2px 6px', 
                                  borderRadius: '10px',
                                  fontWeight: '600'
                                }}>
                                  LIÉ
                                </span>
                              )}
                              
                              {analysis?.status === 'exact_match' && (
                                <span style={{ 
                                  fontSize: '10px', 
                                  backgroundColor: theme.colors.mediumGrey, 
                                  color: theme.colors.bgDark, 
                                  padding: '2px 6px', 
                                  borderRadius: '10px',
                                  fontWeight: '600'
                                }}>
                                  EXACT
                                </span>
                              )}
                              
                              {analysis?.status === 'similar_match' && analysis?.similarMatches?.length > 0 && (
                                <span style={{ 
                                  fontSize: '10px', 
                                  backgroundColor: theme.colors.darkGrey, 
                                  color: theme.colors.bgDark, 
                                  padding: '2px 6px', 
                                  borderRadius: '10px',
                                  fontWeight: '600'
                                }}>
                                  {analysis.similarMatches.length} SIM.
                                </span>
                              )}
                              
                              {analysis?.occurrences > 1 && (
                                <span style={{ 
                                  fontSize: '10px', 
                                  backgroundColor: theme.colors.mediumGrey, 
                                  color: theme.colors.bgDark, 
                                  padding: '2px 6px', 
                                  borderRadius: '10px',
                                  fontWeight: '600'
                                }}>
                                  {analysis.occurrences}x
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '12px', border: 'none' }}>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {analysis?.status === 'linked' ? (
                                <>
                                  <button
                                    onClick={() => openClientSidebar(analysis.linkedClient)}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: theme.colors.lightGrey,
                                      color: theme.colors.bgDark,
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    👁️ Voir
                                  </button>
                                  <button
                                    onClick={() => unlinkCharter(row)}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: theme.colors.darkGrey,
                                      color: theme.colors.bgDark,
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    🔗 Délier
                                  </button>
                                </>
                              ) : analysis?.status === 'exact_match' ? (
                                <>
                                  <button
                                    onClick={() => openClientSidebar(analysis.exactMatch)}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: theme.colors.lightGrey,
                                      color: theme.colors.bgDark,
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    👁️ Voir
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedTargetClient(analysis.exactMatch.client_id);
                                      const charterKey = getCharterKey(row);
                                      saveCharterLink(charterKey, analysis.exactMatch.client_id);
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: theme.colors.mediumGrey,
                                      color: theme.colors.bgDark,
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    🔗 Lier
                                  </button>
                                </>
                              ) : analysis?.status === 'similar_match' ? (
                                <>
                                  <button
                                    onClick={() => openLinkModal(row)}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: theme.colors.darkGrey,
                                      color: theme.colors.bgDark,
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    🔍 Assigner
                                  </button>
                                  <button
                                    onClick={() => {
                                      setShowCreateFromCharter(true);
                                      setCharterToLink(row);
                                      openClientSidebar(null, row.client_name);
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: theme.colors.white,
                                      color: theme.colors.bgDark,
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    ➕ Créer
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => {
                                      setShowCreateFromCharter(true);
                                      setCharterToLink(row);
                                      openClientSidebar(null, row.client_name);
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: theme.colors.white,
                                      color: theme.colors.bgDark,
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    ➕ Nouveau
                                  </button>
                                  <button
                                    onClick={() => openLinkModal(row)}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: theme.colors.darkGrey,
                                      color: theme.colors.bgDark,
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    🔍 Chercher
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Bottom Pagination Controls */}
              <PaginationControls />
            </>
          )}
        </>
      )}

      {/* Client Management Mode */}
      {currentMode === 'clients' && (
        <div>
          {/* Client Header with Search and Actions */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '20px',
            gap: '20px'
          }}>
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: '0 0 15px 0', color: theme.colors.textPrimary, fontSize: '24px', fontWeight: '600' }}>
                👥 Gestion des Clients
              </h2>
              <div style={{ maxWidth: '400px' }}>
                <input
                  type="text"
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  placeholder="🔍 Rechercher par nom, email, téléphone ou notes..."
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.colors.glassBorder}`,
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease'
                  }}
                />
              </div>
            </div>
            
            <button
              onClick={() => openClientSidebar()}
              style={{
                padding: '12px 24px',
                backgroundColor: theme.colors.lightGrey,
                color: theme.colors.bgDark,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              ➕ Nouveau Client
            </button>
          </div>

          {/* Enhanced Client Statistics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '16px',
              backgroundColor: theme.colors.glassMedium,
              borderRadius: '8px',
              border: `1px solid ${theme.colors.glassBorder}`,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '600', color: theme.colors.textPrimary, marginBottom: '4px' }}>
                {formatIntegerNumber(clients.length)}
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>Total Clients</div>
            </div>
            <div style={{
              padding: '16px',
              backgroundColor: theme.colors.glassMedium,
              borderRadius: '8px',
              border: `1px solid ${theme.colors.glassBorder}`,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '600', color: theme.colors.white, marginBottom: '4px' }}>
                {formatIntegerNumber(filteredClients.length)}
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>Clients Affichés</div>
            </div>
            <div style={{
              padding: '16px',
              backgroundColor: theme.colors.glassMedium,
              borderRadius: '8px',
              border: `1px solid ${theme.colors.glassBorder}`,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '600', color: theme.colors.textPrimary, marginBottom: '4px' }}>
                {formatIntegerNumber(Object.keys(charterLinks).length)}
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>Charters Liés</div>
            </div>
            <div style={{
              padding: '16px',
              backgroundColor: theme.colors.glassMedium,
              borderRadius: '8px',
              border: `1px solid ${theme.colors.glassBorder}`,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '600', color: theme.colors.textPrimary, marginBottom: '4px' }}>
                €{formatNumber(
                  clients.reduce((total, client) => {
                    const stats = getClientStats(client.client_id);
                    return total + stats.total;
                  }, 0)
                )}
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>CA Clients Liés</div>
            </div>
          </div>

          {/* Enhanced Clients List */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
            {paginatedClients.map(client => {
              const clientStats = getClientStats(client.client_id);
              
              return (
                <div key={client.client_id} style={{
                  padding: '20px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: `1px solid ${theme.colors.glassBorder}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, color: theme.colors.textPrimary, fontSize: '18px', fontWeight: '600' }}>
                      {client.client_name}
                    </h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openClientSidebar(client)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: theme.colors.white,
                          color: theme.colors.bgDark,
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        ✏️ Modifier
                      </button>
                      <button
                        onClick={() => openClientSidebar(client)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: theme.colors.lightGrey,
                          color: theme.colors.bgDark,
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        👁️ Voir
                      </button>
                    </div>
                  </div>
                  
                  {client.email && (
                    <div style={{ 
                      margin: '8px 0', 
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ color: theme.colors.textPrimary }}>📧</span>
                      {client.email}
                    </div>
                  )}
                  
                  {client.phone && (
                    <div style={{ 
                      margin: '8px 0', 
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ color: theme.colors.white }}>📞</span>
                      {client.phone}
                    </div>
                  )}
                  
                  {/* Enhanced client stats */}
                  <div style={{ 
                    marginTop: '16px', 
                    padding: '12px', 
                    backgroundColor: clientStats.count > 0 ? '#f6ffed' : '#f8f9fa', 
                    borderRadius: '8px',
                    border: clientStats.count > 0 ? '1px solid #b7eb8f' : '1px solid #e1e5e9'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: '20px', 
                          fontWeight: '600', 
                          color: clientStats.count > 0 ? '#52c41a' : '#8c8c8c'
                        }}>
                          {clientStats.count}
                        </div>
                        <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>Charters Liés</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: clientStats.total > 0 ? '#52c41a' : '#8c8c8c'
                        }}>
                          €{formatNumber(clientStats.total)}
                        </div>
                        <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>CA Total</div>
                      </div>
                    </div>
                    
                    {clientStats.count === 0 && (
                      <div style={{ 
                        marginTop: '8px', 
                        textAlign: 'center',
                        fontSize: '12px',
                        color: theme.colors.textPrimary,
                        fontStyle: 'italic'
                      }}>
                        Aucun charter lié
                      </div>
                    )}
                  </div>
                  
                  {client.notes && (
                    <div style={{ 
                      marginTop: '12px', 
                      fontSize: '13px', 
                      color: theme.colors.textTertiary,
                      fontStyle: 'italic',
                      lineHeight: '1.4',
                      backgroundColor: theme.colors.glassLight,
                      padding: '8px',
                      borderRadius: '4px',
                      borderLeft: '3px solid #1890ff'
                    }}>
                      "{client.notes.substring(0, 120)}{client.notes.length > 120 ? '...' : ''}"
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Client Pagination */}
          <ClientPaginationControls />

          {clients.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px', 
              backgroundColor: theme.colors.glassLight, 
              borderRadius: '12px',
              border: '2px dashed #d9d9d9',
              marginTop: '20px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
              <p style={{ fontSize: '18px', color: theme.colors.textSecondary, marginBottom: '8px' }}>
                Aucun client enregistré
              </p>
              <p style={{ fontSize: '14px', color: theme.colors.textTertiary, marginBottom: '20px' }}>
                Utilisez l'analyse des charters pour ajouter des clients importants à votre base de données.
              </p>
              <button
                onClick={() => openClientSidebar()}
                style={{
                  padding: '12px 24px',
                  backgroundColor: theme.colors.white,
                  color: theme.colors.bgDark,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                ➕ Créer votre premier client
              </button>
            </div>
          )}

          {filteredClients.length === 0 && clients.length > 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px', 
              backgroundColor: theme.colors.glassMedium, 
              borderRadius: '8px',
              marginTop: '20px'
            }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>🔍</div>
              <p style={{ fontSize: '16px', color: theme.colors.textPrimary, margin: 0 }}>
                Aucun client trouvé pour "{clientSearchTerm}"
              </p>
            </div>
          )}
        </div>
      )}

      {/* Enhanced Right Sidebar */}
      {showClientSidebar && (
        <>
          {/* Overlay */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0,0.45)',
              zIndex: 998,
              transition: 'opacity 0.3s ease'
            }}
            onClick={closeSidebar}
          />
          
          {/* Sidebar */}
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '520px',
            height: '100%',
            backgroundColor: 'white',
            boxShadow: '-8px 0 28px rgba(0,0,0,0.12)',
            zIndex: 999,
            overflowY: 'auto',
            transform: showClientSidebar ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)'
          }}>
            {/* Sidebar Header */}
            <div style={{
              padding: '24px',
              backgroundColor: showCreateFromCharter ? '#52c41a' : '#1890ff',
              color: theme.colors.bgDark,
              position: 'sticky',
              top: 0,
              zIndex: 1000,
              borderBottom: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600' }}>
                    {selectedClient ? '✏️ Modifier le Client' : 
                     showCreateFromCharter ? '🔗 Nouveau Client depuis Charter' : 
                     '➕ Nouveau Client'}
                  </h2>
                  <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                    {selectedClient ? 'Mettre à jour les informations client' : 
                     showCreateFromCharter ? 'Créer et lier automatiquement au charter' :
                     'Ajouter un nouveau client à la base de données'}
                  </p>
                  {showCreateFromCharter && charterToLink && (
                    <div style={{ 
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      borderRadius: '6px',
                      fontSize: '13px'
                    }}>
                      Charter: {charterToLink.charter_date} • €{formatNumber(charterToLink.total_amount)}
                    </div>
                  )}
                </div>
                <button
                  onClick={closeSidebar}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: theme.colors.bgDark,
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '8px',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Sidebar Content */}
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gap: '24px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.colors.textPrimary, fontSize: '14px' }}>
                    Nom du Client *
                  </label>
                  <input
                    type="text"
                    value={clientForm.client_name}
                    onChange={(e) => setClientForm(prev => ({ ...prev, client_name: e.target.value }))}
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: `1px solid ${theme.colors.glassBorder}`,
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d9d9d9';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="Entrez le nom du client"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.colors.textPrimary, fontSize: '14px' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={clientForm.email}
                      onChange={(e) => setClientForm(prev => ({ ...prev, email: e.target.value }))}
                      style={{ 
                        width: '100%', 
                        padding: '12px 16px', 
                        borderRadius: '8px', 
                        border: `1px solid ${theme.colors.glassBorder}`,
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1890ff';
                        e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d9d9d9';
                        e.target.style.boxShadow = 'none';
                      }}
                      placeholder="client@email.com"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.colors.textPrimary, fontSize: '14px' }}>
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={clientForm.phone}
                      onChange={(e) => setClientForm(prev => ({ ...prev, phone: e.target.value }))}
                      style={{ 
                        width: '100%', 
                        padding: '12px 16px', 
                        borderRadius: '8px', 
                        border: `1px solid ${theme.colors.glassBorder}`,
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1890ff';
                        e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d9d9d9';
                        e.target.style.boxShadow = 'none';
                      }}
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.colors.textPrimary, fontSize: '14px' }}>
                    Informations de Contact
                  </label>
                  <textarea
                    value={clientForm.contact_info}
                    onChange={(e) => setClientForm(prev => ({ ...prev, contact_info: e.target.value }))}
                    rows={3}
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: `1px solid ${theme.colors.glassBorder}`,
                      fontSize: '14px',
                      resize: 'vertical',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      fontFamily: 'inherit'
                    }}
                    placeholder="Adresse, préférences de contact, etc."
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d9d9d9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.colors.textPrimary, fontSize: '14px' }}>
                    Notes
                  </label>
                  <textarea
                    value={clientForm.notes}
                    onChange={(e) => setClientForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={4}
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: `1px solid ${theme.colors.glassBorder}`,
                      fontSize: '14px',
                      resize: 'vertical',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      fontFamily: 'inherit'
                    }}
                    placeholder="Notes sur le client, préférences, historique..."
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d9d9d9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.colors.textPrimary, fontSize: '14px' }}>
                    Autres Informations
                  </label>
                  <textarea
                    value={clientForm.other_info}
                    onChange={(e) => setClientForm(prev => ({ ...prev, other_info: e.target.value }))}
                    rows={3}
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: `1px solid ${theme.colors.glassBorder}`,
                      fontSize: '14px',
                      resize: 'vertical',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      fontFamily: 'inherit'
                    }}
                    placeholder="Informations supplémentaires..."
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1890ff';
                      e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d9d9d9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Image upload section for existing clients */}
                {selectedClient && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: theme.colors.textPrimary, fontSize: '14px' }}>
                      📸 Photos du Client
                    </label>
                    
                    <div style={{
                      border: '2px dashed #d9d9d9',
                      borderRadius: '8px',
                      padding: '24px',
                      textAlign: 'center',
                      marginBottom: '16px',
                      backgroundColor: theme.colors.glassLight,
                      transition: 'all 0.2s ease',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && selectedClient) {
                            uploadClientImage(file, selectedClient.client_id);
                          }
                        }}
                        style={{ 
                          width: '100%', 
                          padding: '12px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      />
                      <div style={{ marginTop: '8px', fontSize: '12px', color: theme.colors.textTertiary }}>
                        Formats supportés: JPG, PNG, WEBP
                      </div>
                      {uploadingImage && (
                        <div style={{ 
                          marginTop: '12px',
                          padding: '8px 16px',
                          backgroundColor: theme.colors.glassMedium,
                          borderRadius: '6px',
                          color: theme.colors.textPrimary,
                          fontSize: '14px',
                          fontWeight: '500'
                        }}>
                          📤 Téléchargement en cours...
                        </div>
                      )}
                    </div>
                    
                    {clientImages.length > 0 && (
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                        gap: '12px',
                        marginTop: '16px'
                      }}>
                        {clientImages.map(image => (
                          <div key={image.image_id} style={{ position: 'relative' }}>
                            <img
                              src={`https://vlakljvouimjemxbcnmo.supabase.co/storage/v1/object/public/media/${image.image_path}`}
                              alt={image.caption || 'Client photo'}
                              style={{
                                width: '100%',
                                height: '120px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: image.is_primary ? '3px solid #52c41a' : '2px solid #f0f0f0',
                                transition: 'all 0.2s ease'
                              }}
                            />
                            {image.is_primary && (
                              <div style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                backgroundColor: theme.colors.lightGrey,
                                color: theme.colors.bgDark,
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: '600',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}>
                                Principal
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Show charter history if client exists */}
                {selectedClient && (
                  <div style={{ 
                    marginTop: '24px', 
                    padding: '20px', 
                    backgroundColor: theme.colors.glassMedium, 
                    borderRadius: '8px',
                    border: `1px solid ${theme.colors.glassBorder}`
                  }}>
                    <h4 style={{ margin: '0 0 16px 0', color: theme.colors.textPrimary, fontSize: '16px', fontWeight: '600' }}>
                      📊 Charters Liés
                    </h4>
                    {(() => {
                      const clientCharters = getClientCharters(selectedClient.client_id);
                      return (
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <div style={{ 
                              padding: '16px', 
                              backgroundColor: 'white', 
                              borderRadius: '8px',
                              textAlign: 'center',
                              border: `1px solid ${theme.colors.glassBorder}`
                            }}>
                              <div style={{ fontSize: '28px', fontWeight: '600', color: theme.colors.textPrimary, marginBottom: '4px' }}>
                                {clientCharters.length}
                              </div>
                              <div style={{ fontSize: '12px', color: theme.colors.textSecondary, fontWeight: '500' }}>Charters Liés</div>
                            </div>
                            <div style={{ 
                              padding: '16px', 
                              backgroundColor: 'white', 
                              borderRadius: '8px',
                              textAlign: 'center',
                              border: `1px solid ${theme.colors.glassBorder}`
                            }}>
                              <div style={{ fontSize: '20px', fontWeight: '600', color: theme.colors.white, marginBottom: '4px' }}>
                                €{formatNumber(clientCharters.reduce((sum, charter) => sum + charter.total_amount, 0))}
                              </div>
                              <div style={{ fontSize: '12px', color: theme.colors.textSecondary, fontWeight: '500' }}>Total</div>
                            </div>
                          </div>
                          
                          {clientCharters.length > 0 && (
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: theme.colors.textPrimary, marginBottom: '12px' }}>
                                Charters liés:
                              </div>
                              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {clientCharters.slice(0, 5).map((charter, index) => (
                                  <div key={index} style={{ 
                                    padding: '12px',
                                    backgroundColor: 'white',
                                    borderRadius: '6px',
                                    marginBottom: '8px',
                                    fontSize: '13px',
                                    border: `1px solid ${theme.colors.glassBorder}`,
                                    transition: 'all 0.2s ease'
                                  }}>
                                    <div style={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between', 
                                      alignItems: 'center',
                                      marginBottom: '4px'
                                    }}>
                                      <div style={{ fontWeight: '600', color: theme.colors.textPrimary }}>
                                        {charter.charter_date}
                                      </div>
                                      <div style={{ 
                                        fontWeight: '600', 
                                        color: theme.colors.white,
                                        fontSize: '14px'
                                      }}>
                                        €{formatNumber(charter.total_amount)}
                                      </div>
                                    </div>
                                    <div style={{ color: theme.colors.textSecondary, fontSize: '12px' }}>
                                      {charter.charter_type} • {charter.payment_method}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {clientCharters.length > 5 && (
                                <div style={{ 
                                  textAlign: 'center', 
                                  marginTop: '8px',
                                  fontSize: '12px',
                                  color: theme.colors.textTertiary
                                }}>
                                  Et {clientCharters.length - 5} autres charters...
                                </div>
                              )}
                            </div>
                          )}
                          
                          {clientCharters.length === 0 && (
                            <div style={{ 
                              textAlign: 'center',
                              padding: '20px',
                              color: theme.colors.textTertiary,
                              fontSize: '14px',
                              fontStyle: 'italic'
                            }}>
                              Aucun charter lié à ce client
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Sidebar Footer with Actions */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '12px', 
                marginTop: '32px',
                paddingTop: '24px',
                borderTop: '1px solid #f0f0f0'
              }}>
                <button
                  onClick={closeSidebar}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: theme.colors.glassLight,
                    color: theme.colors.textSecondary,
                    border: `1px solid ${theme.colors.glassBorder}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e6f7ff';
                    e.target.style.borderColor = '#91d5ff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.style.borderColor = '#d9d9d9';
                  }}
                >
                  Annuler
                </button>
                
                <button
                  onClick={saveClient}
                  disabled={!clientForm.client_name.trim()}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: clientForm.client_name.trim() ? 
                      (showCreateFromCharter ? '#52c41a' : '#1890ff') : '#f5f5f5',
                    color: clientForm.client_name.trim() ? 'white' : '#bfbfbf',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: clientForm.client_name.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    boxShadow: clientForm.client_name.trim() ? 
                      (showCreateFromCharter ? '0 2px 8px rgba(82, 196, 26, 0.3)' : '0 2px 8px rgba(24, 144, 255, 0.3)') : 'none'
                  }}
                >
                  {selectedClient ? '✅ Mettre à jour' : 
                   showCreateFromCharter ? '🔗 Créer et Lier' : 
                   '➕ Créer'} Client
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Enhanced Link Modal */}
      {showLinkModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          transition: 'opacity 0.3s ease'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '600px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
              🔗 Lier le Charter à un Client
            </h3>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ 
                padding: '16px',
                backgroundColor: theme.colors.glassMedium,
                borderRadius: '8px',
                border: `1px solid ${theme.colors.glassBorder}`,
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '4px' }}>Charter à lier:</div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: theme.colors.textPrimary }}>
                  {charterToLink?.client_name}
                </div>
                <div style={{ fontSize: '13px', color: theme.colors.textTertiary, marginTop: '4px' }}>
                  {charterToLink?.charter_date} • €{formatNumber(charterToLink?.total_amount)} • {charterToLink?.charter_type}
                </div>
              </div>
              <p style={{ margin: '0 0 16px 0', color: theme.colors.textSecondary, fontSize: '14px' }}>
                Recherchez et sélectionnez le client existant auquel vous voulez lier ce charter:
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: theme.colors.textPrimary }}>
                🔍 Rechercher un client:
              </label>
              <input
                type="text"
                value={linkSearchTerm}
                onChange={(e) => setLinkSearchTerm(e.target.value)}
                placeholder="Tapez pour rechercher un client..."
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.colors.glassBorder}`,
                  fontSize: '14px',
                  marginBottom: '12px',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#1890ff'}
                onBlur={(e) => e.target.style.borderColor = '#d9d9d9'}
              />
              
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto', 
                border: '1px solid #f0f0f0', 
                borderRadius: '8px',
                backgroundColor: theme.colors.glassLight
              }}>
                {filteredLinkClients.length > 0 ? (
                  <>
                    {filteredLinkClients.map(client => {
                      const clientCharters = getClientCharters(client.client_id);
                      const isRelevant = client.client_name.toLowerCase().includes(charterToLink?.client_name.toLowerCase() || '') ||
                                       (charterToLink?.client_name.toLowerCase() || '').includes(client.client_name.toLowerCase());
                      
                      return (
                        <div
                          key={client.client_id}
                          onClick={() => setSelectedTargetClient(client.client_id)}
                          style={{
                            padding: '16px',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            backgroundColor: selectedTargetClient === client.client_id ? '#e6f7ff' : 'white',
                            transition: 'background-color 0.2s ease',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: isRelevant ? '2px solid #52c41a' : '1px solid transparent',
                            margin: '2px',
                            borderRadius: '6px'
                          }}
                          onMouseEnter={(e) => {
                            if (selectedTargetClient !== client.client_id) {
                              e.target.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedTargetClient !== client.client_id) {
                              e.target.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontWeight: '500', 
                              color: theme.colors.textPrimary, 
                              fontSize: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              {isRelevant && <span style={{ color: theme.colors.white, fontSize: '16px' }}>⭐</span>}
                              {client.client_name}
                              {clientCharters.length > 0 && (
                                <span style={{
                                  fontSize: '10px',
                                  backgroundColor: theme.colors.white,
                                  color: theme.colors.bgDark,
                                  padding: '2px 6px',
                                  borderRadius: '10px',
                                  fontWeight: '600'
                                }}>
                                  {clientCharters.length} charters
                                </span>
                              )}
                            </div>
                            {client.email && (
                              <div style={{ fontSize: '12px', color: theme.colors.textTertiary, marginTop: '2px' }}>
                                📧 {client.email}
                              </div>
                            )}
                            {client.phone && (
                              <div style={{ fontSize: '12px', color: theme.colors.textTertiary, marginTop: '2px' }}>
                                📞 {client.phone}
                              </div>
                            )}
                            {clientCharters.length > 0 && (
                              <div style={{ fontSize: '12px', color: theme.colors.white, marginTop: '4px', fontWeight: '500' }}>
                                CA: €{formatNumber(clientCharters.reduce((sum, charter) => sum + charter.total_amount, 0))}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openClientSidebar(client);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: theme.colors.glassLight,
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                color: theme.colors.textSecondary
                              }}
                            >
                              👁️ Voir
                            </button>
                            {selectedTargetClient === client.client_id && (
                              <span style={{ 
                                padding: '6px 12px',
                                backgroundColor: theme.colors.lightGrey,
                                color: theme.colors.bgDark,
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}>
                                ✓ Sélectionné
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Create new client option */}
                    <div
                      onClick={() => {
                        setShowLinkModal(false);
                        setShowCreateFromCharter(true);
                        openClientSidebar(null, charterToLink?.client_name);
                      }}
                      style={{
                        padding: '16px',
                        cursor: 'pointer',
                        backgroundColor: theme.colors.glassMedium,
                        transition: 'background-color 0.2s ease',
                        textAlign: 'center',
                        border: `2px dashed ${theme.colors.glassBorderHover}`,
                        borderRadius: '6px',
                        margin: '8px'
                      }}
                    >
                      <div style={{ 
                        color: theme.colors.textPrimary, 
                        fontWeight: '600',
                        fontSize: '14px',
                        marginBottom: '4px'
                      }}>
                        ➕ Créer un nouveau client
                      </div>
                      <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                        "{charterToLink?.client_name}" n'existe pas ? Créez-le !
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ 
                    padding: '40px', 
                    textAlign: 'center', 
                    color: theme.colors.textTertiary,
                    fontSize: '14px'
                  }}>
                    <div style={{ fontSize: '36px', marginBottom: '12px' }}>🔍</div>
                    {linkSearchTerm ? (
                      <>
                        <div>Aucun client trouvé pour "{linkSearchTerm}"</div>
                        <button
                          onClick={() => {
                            setShowLinkModal(false);
                            setShowCreateFromCharter(true);
                            openClientSidebar(null, charterToLink?.client_name);
                          }}
                          style={{
                            marginTop: '12px',
                            padding: '8px 16px',
                            backgroundColor: theme.colors.white,
                            color: theme.colors.bgDark,
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          ➕ Créer "{charterToLink?.client_name}"
                        </button>
                      </>
                    ) : (
                      'Commencez à taper pour rechercher'
                    )}
                  </div>
                )}
              </div>
            </div>

            {selectedTargetClient && (
              <div style={{
                padding: '16px',
                backgroundColor: theme.colors.glassMedium,
                borderRadius: '8px',
                marginBottom: '24px',
                border: `1px solid ${theme.colors.glassBorder}`
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: theme.colors.textPrimary, fontSize: '14px', fontWeight: '600' }}>
                  ✅ Aperçu du lien:
                </h4>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}>
                  <span style={{ 
                    padding: '4px 8px',
                    backgroundColor: theme.colors.darkGrey,
                    color: theme.colors.bgDark,
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    Charter: {charterToLink?.client_name}
                  </span>
                  <span style={{ color: theme.colors.textTertiary }}>→</span>
                  <span style={{ 
                    padding: '4px 8px',
                    backgroundColor: theme.colors.lightGrey,
                    color: theme.colors.bgDark,
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    Client: {clients.find(c => c.client_id === selectedTargetClient)?.client_name}
                  </span>
                </div>
                <p style={{ margin: '0', fontSize: '12px', color: theme.colors.textSecondary }}>
                  Le charter du {charterToLink?.charter_date} sera lié à ce client
                </p>
              </div>
            )}

            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px' 
            }}>
              <button
                onClick={() => {
                  setShowLinkModal(false);
                  setCharterToLink(null);
                  setSelectedTargetClient('');
                  setLinkSearchTerm('');
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: theme.colors.glassLight,
                  color: theme.colors.textSecondary,
                  border: `1px solid ${theme.colors.glassBorder}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Annuler
              </button>
              
              <button
                onClick={linkCharter}
                disabled={!selectedTargetClient}
                style={{
                  padding: '12px 24px',
                  backgroundColor: selectedTargetClient ? '#52c41a' : '#f5f5f5',
                  color: selectedTargetClient ? 'white' : '#bfbfbf',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: selectedTargetClient ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: selectedTargetClient ? '0 2px 8px rgba(82, 196, 26, 0.3)' : 'none'
                }}
              >
                🔗 Lier Charter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default ImprovedCharterClientSystem;